import {
  Button,
  Icon,
  TextField,
  Typography,
  useSnackbar,
} from "@mindlercare/ui-web";
import { FormEvent, useState } from "react";
import { useInternalAuth } from "../AuthenticationProvider";
import { LinkButton } from "../components/LinkButton";
import { useTranslation } from "../hooks/useTranslation";
import { useStateNavigator } from "../hooks/useStateNavigator";
import { BackToLogin } from "./BackToLogin";

export const ResetCognitoPassword = () => {
  const { t } = useTranslation();
  const { goBack } = useStateNavigator();
  const { forgotPassword, forgotPasswordSubmit, resendMFA } = useInternalAuth();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [errorMessagePassword, setErrorMessagePassword] = useState<string>("");
  const [errorMessageConfirmPassword, setErrorMessageConfirmPassword] =
    useState<string>("");
  const [errorMessageCode, setErrorMessageCode] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [requestedPasswordReset, setRequestedPasswordReset] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!requestedPasswordReset) {
      forgotPassword(email.toLowerCase());
      setRequestedPasswordReset(true);
    } else {
      // Validate passwords match
      if (password !== confirmPassword) {
        setErrorMessageConfirmPassword(t("PASSWORDS_DO_NOT_MATCH"));
        return;
      }

      try {
        await forgotPasswordSubmit(email.toLowerCase(), code, password);
        goBack();
      } catch (error: unknown) {
        if (error instanceof Error && error.name === "CodeMismatchException") {
          setErrorMessageCode(error.message);
        } else if (
          error instanceof Error &&
          (error.name === "InvalidPasswordException" ||
            error.name === "LimitExceededException")
        ) {
          setErrorMessagePassword(error.message);
        } else {
          console.error(error);
          setErrorMessageCode(t("ERROR_UNKNOWN"));
          setErrorMessagePassword(t("ERROR_UNKNOWN"));
        }
      }
    }
  };

  return (
    <>
      <BackToLogin />
      <form onSubmit={handleSubmit}>
        <Typography variant="h3">{t("RESET_YOUR_PASSWORD")}</Typography>
        {!requestedPasswordReset ? (
          <>
            <div style={{ margin: "24px 0px" }}>
              <TextField
                placeholder={t("EMAIL_PLACEHOLDER")}
                label={t("EMAIL")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                hideSensitiveData={true}
              />
            </div>
            <Button variant="primary" disabled={!email} type="submit">
              {t("RESET_PASSWORD")}
            </Button>
          </>
        ) : (
          <>
            <Typography
              variant="body"
              style={{ marginTop: "8px", marginBottom: "24px" }}
            >
              {t("CODE_HAS_BEEN_SENT")}
            </Typography>
            <div style={{ marginBottom: "12px" }}>
              <TextField
                placeholder={t("CONFIRMATION_CODE")}
                label={`${t("CONFIRMATION_CODE")}*`}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                hideSensitiveData={true}
                error={errorMessageCode.length > 0}
                errorMessage={errorMessageCode}
              />
            </div>
            <div style={{ marginBottom: "12px" }}>
              <TextField
                placeholder={t("NEW_PASSWORD_PLACEHOLDER")}
                label={`${t("NEW_PASSWORD")}*`}
                type={showPassword ? "default" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                hideSensitiveData={true}
                error={errorMessagePassword.length > 0}
                errorMessage={errorMessagePassword}
                icon={
                  showPassword ? <Icon.VisibilityOff /> : <Icon.VisibilityOn />
                }
                onIconClick={() => setShowPassword(!showPassword)}
              />
            </div>
            <div style={{ marginBottom: "24px" }}>
              <TextField
                placeholder={t("CONFIRM_PASSWORD_PLACEHOLDER")}
                label={`${t("CONFIRM_PASSWORD")}*`}
                type={showConfirmPassword ? "default" : "password"}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setErrorMessageConfirmPassword("");
                }}
                hideSensitiveData={true}
                error={errorMessageConfirmPassword.length > 0}
                errorMessage={errorMessageConfirmPassword}
                icon={
                  showConfirmPassword ? (
                    <Icon.VisibilityOff />
                  ) : (
                    <Icon.VisibilityOn />
                  )
                }
                onIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
              />
            </div>
            <div style={{ margin: "24px 0px" }}>
              <LinkButton
                type="button"
                underline
                onClick={() => {
                  enqueueSnackbar({ text: t("CODE_HAS_BEEN_SENT") });
                  resendMFA();
                }}
              >
                {t("RESEND_CODE")}
              </LinkButton>
            </div>
            <Button
              variant="primary"
              disabled={!code || !password || !confirmPassword}
              type="submit"
            >
              {t("SUBMIT")}
            </Button>
          </>
        )}
      </form>
    </>
  );
};
